import { Suspense } from "react";
import { useLocation } from "react-router";
import PageShell from "../../../design-system/components/PageShell/PageShell";
import Text from "../../../design-system/primitives/components/Text/Text";
import { Spinner } from "../../../design-system/utils/Spinner/Spinner";
import { ROUTES } from "../../../routes.js";
import { useStorageState } from "../../../lib/storage";
import type { Schedule } from "../../../api/spark";
import { ToolbarContainer } from "../toolbar/ToolbarContainer";
import type { AnySparkBag } from "../sparkBag";
import type { NavBag } from "./components/HighLevelNavigation/HighLevelNavigation";
import { HighLevelNavigation } from "./components/HighLevelNavigation/HighLevelNavigation";
import { StageSchedule } from "./components/StageSchedule/StageSchedule";
import styles from "./SparkOverview.css";

interface SparkOverviewProps {
  /** The spark's title, shown in the page header. */
  title: string;
  /** Optional on the workshop projection, so it is passed down as-is. */
  sparkSlug: string | undefined;
  /** The spark's structure. Absent or empty renders the "no content" note. */
  phases: Schedule | undefined;
  /** Base url the stage's module links are built from. */
  baseUrl: string;
  /**
   * Greys out phases/stages/modules the viewer may not open. Set in the demo,
   * where a spark is opened without a workshop behind it.
   */
  disableSome?: boolean;
}

/**
 * The overview page itself — the spark's root screen, listing its phases and
 * stages next to the selected stage's modules.
 *
 * Deliberately free of any spark data beyond what it renders, so it can be
 * rendered from a plain structure (stories) as well as from a spark bag (the
 * default export below).
 */
export const SparkOverview = ({
  title,
  sparkSlug,
  phases,
  baseUrl,
  disableSome = false,
}: SparkOverviewProps) => {
  const { pathname } = useLocation();

  const [phaseIndex, setPhaseIndex] = useStorageState<number>(
    `lastPhaseIndex-${pathname}`,
    0,
  );

  const [stageIndex, setStageIndex] = useStorageState<number>(
    `lastStageIndex-${pathname}`,
    0,
  );

  const handleStageClick = (nextPhaseIndex: number, nextStageIndex: number) => {
    setPhaseIndex(nextPhaseIndex);
    setStageIndex(nextStageIndex);
  };

  if (!phases || phases.length === 0) {
    return <p>Lernreise hat noch keine Inhalte.</p>;
  }

  // A stored index can point past the end of a spark whose structure changed
  // since the last visit — fall back to the first phase/stage rather than
  // rendering an empty page.
  const currentPhaseIndex = phases[phaseIndex] ? phaseIndex : 0;
  const stages = phases[currentPhaseIndex].stages;
  const currentStageIndex = stages[stageIndex] ? stageIndex : 0;

  const nav: NavBag = {
    currentPhaseIndex,
    currentStageIndex,
    disableSome,
    onStageClick: handleStageClick,
  };

  return (
    // The overview is the spark's root page, so there is nowhere to go back
    // to — hence no title button.
    <PageShell
      titleSlot={
        <Text variant="h4" id="title" data-cy="cy-workshopansicht-heading">
          {title}
        </Text>
      }
      surface="dark"
    >
      <div className={styles.columns}>
        <div className={styles.leftColumn}>
          <HighLevelNavigation phases={phases} nav={nav} />
        </div>
        <div className={styles.rightColumn}>
          {/* Own boundary: the schedule refetches on every stage click, and
              without this the whole overview would fall back to the route
              level spinner — including the navigation being clicked. */}
          <Suspense fallback={<Spinner />}>
            <StageSchedule
              sparkSlug={sparkSlug}
              stageSlug={stages[currentStageIndex]?.slug}
              baseUrl={baseUrl}
              disableSome={disableSome}
            />
          </Suspense>
        </div>
      </div>
    </PageShell>
  );
};

interface SparkOverviewScreenProps {
  /** The spark data, handed down by the shell rather than read off a context. */
  bag: AnySparkBag;
}

/**
 * The overview as a screen: the spark's root page plus its toolbar. Serves a
 * Lernreise and the demo alike — the demo has no workshop, so its step urls
 * hang off the demo route and the parts that only work inside a real Lernreise
 * are greyed out.
 */
const SparkOverviewScreen = ({ bag }: SparkOverviewScreenProps) => {
  const { workshop, spark, structure } = bag;
  const baseUrl = workshop
    ? ROUTES.spark.getUrl({
        workshopSlug: workshop.slug,
        sparkSlug: spark.slug,
      })
    : ROUTES.demoLernreise.getUrl({ sparkSlug: spark.slug });

  return (
    <>
      <SparkOverview
        title={spark.inAppTitle}
        sparkSlug={spark.slug}
        phases={structure}
        baseUrl={baseUrl}
        disableSome={workshop === null}
      />
      <ToolbarContainer bag={bag} />
    </>
  );
};

export default SparkOverviewScreen;
